import { Form, Formik, FormikHelpers } from "formik";
import React, { useContext } from "react";
import { useMessageSource } from "react-message-source";
import { ApplicationFlows } from "../../route/application/authenticated/application-flow/types";
import ApplicationPageContent from "../../ui/layout/page/content/application";
import ContentFooter from "../../ui/layout/page/content/footer";
import NavigationBar from "../../ui/layout/page/content/footer/navigation-bar";
import ContentHeader from "../../ui/layout/page/content/header";
import FormContainer from "../../ui/layout/page/forms-container/form-base-container";
import RadioButtonContainer from "../../ui/layout/page/forms-container/form-item-container/radio-button-container/input-container";
import FlowHeader from "../../ui/widgets/flow-header";
import { mapPersonSameAsToEvent } from "../../util/tracking/eventActionMapper";
import GTM from "../../util/tracking/gtm";
import { EventCategory } from "../../util/tracking/types";
import ChoosePerson from "../shared/components/choose-person";
import ProductImage from "../shared/components/product-image";
import { usePageTitle } from "../shared/hooks/usePageTitle";
import CardholderForm from "./cardholder-form";
import { CardholderModel } from "./cardholder-form/types";
import { CardholderBasicInformationProps, CardholderPageModel } from "./types";
import useValidationSchema from "./validator";
import { LanguageContext } from "../../context/languageContext";
import InvalidFormError, {
  isSubmitErrorMessageShown,
} from "../../ui/layout/page/content/footer/form-error/invalid-form-error";
import { CardholderHelpers } from "./helpers";
import { CancelApplicationContext } from "../../context/cancelApplicationContext";
import { ProductContext } from "../../context/product-context/productContext";
import { CardholderFormikContext } from "./context";

export default function CardholderBasicInformation({
  flowType,
  inLocalState,
  initial,
  context,
  previous,
  next,
}: CardholderBasicInformationProps): React.JSX.Element {
  const { selectedProduct } = useContext(ProductContext);
  const { getMessage } = useMessageSource();
  const { language } = useContext(LanguageContext);
  const { setCancelDialogActive } = useContext(CancelApplicationContext);

  const isAdditionalCardholder = flowType === ApplicationFlows.ADDITIONAL_CA;

  const shouldDisplayCancelButton = !inLocalState;

  const initialValues = CardholderHelpers.resolveInitialValues(
    initial,
    context.adminData,
    flowType,
    context.hasDecentralizedInvoicing,
  );

  const limit = context.limit;

  const onCancel = () => setCancelDialogActive(true);

  const shouldDisplayBackButton = () => {
    return !(isAdditionalCardholder && !inLocalState);
  };

  const handleFormikOnSubmit = async (
    values: CardholderPageModel,
    formikActions: FormikHelpers<CardholderPageModel>,
  ) => {
    await next(values);
    formikActions.setSubmitting(false);
  };

  return (
    <>
      <FlowHeader
        title={usePageTitle(flowType)}
        image={<ProductImage product={selectedProduct?.productRef} />}
        subTitle={context.companyName}
      />
      <ApplicationPageContent>
        <ContentHeader
          title={
            isAdditionalCardholder
              ? getMessage("AdditionalCardholder.title")
              : getMessage("Cardholder.first-title")
          }
        />
        <Formik
          key={language}
          enableReinitialize={true}
          initialValues={initialValues}
          validationSchema={useValidationSchema(limit)}
          onSubmit={handleFormikOnSubmit}
        >
          {({
            values,
            setFieldValue,
            handleSubmit,
            isSubmitting,
            submitCount,
            errors,
            touched,
          }) => {
            const selectedCardholderPerson: CardholderModel =
              values.isSameAsAdmin === "yes"
                ? { ...values.adminAsCardholder }
                : { ...values.cardholder };

            return (
              <Form className="scd-section-pusher-lg">
                <FormContainer>
                  {flowType !== ApplicationFlows.ADDITIONAL_CA && (
                    <RadioButtonContainer>
                      <ChoosePerson
                        title={getMessage(
                          "ChooseFirstCardholderTypeQuestion.description",
                        )}
                        id="isSameAsAdmin"
                        name="isSameAsAdmin"
                        values={[
                          {
                            id: "cardholder-same-as-admin-yes",
                            value: "yes",
                            messageKey: "Cardholder.SAME_AS_CONTACT",
                            person: values.adminAsCardholder,
                          },
                          {
                            id: "cardholder-same-as-admin-no",
                            value: "no",
                            messageKey: "Cardholder.OTHER",
                          },
                        ]}
                        onChange={(_, newValue) => {
                          if (!isAdditionalCardholder) {
                            GTM.trackEvent(
                              EventCategory.CardHolder,
                              mapPersonSameAsToEvent(newValue === "yes"),
                            );
                          }
                        }}
                      />
                    </RadioButtonContainer>
                  )}
                </FormContainer>
                <FormContainer>
                  <CardholderFormikContext.Provider
                    value={{
                      values,
                      errors,
                      touched,
                      setFieldValue,
                    }}
                  >
                    <CardholderForm
                      {...selectedCardholderPerson}
                      isSameAsAdmin={values.isSameAsAdmin === "yes"}
                      isSoleProprietorship={context.isSoleProprietorship}
                      selectedProduct={context.selectedProduct}
                      hasDecentralizedInvoicing={
                        context.hasDecentralizedInvoicing
                      }
                    />
                  </CardholderFormikContext.Provider>
                </FormContainer>
                <ContentFooter>
                  <InvalidFormError
                    isDisplayed={isSubmitErrorMessageShown(submitCount, errors)}
                  />
                  <NavigationBar
                    onCancel={shouldDisplayCancelButton && onCancel}
                    cancelButtonText={getMessage("Onboarding.cancel")}
                    onNext={() => handleSubmit()}
                    nextButtonText={getMessage("Onboarding.continue")}
                    isSubmitting={isSubmitting}
                    onBack={shouldDisplayBackButton() && previous}
                    backButtonText={getMessage("Onboarding.back")}
                  />
                </ContentFooter>
              </Form>
            );
          }}
        </Formik>
      </ApplicationPageContent>
    </>
  );
}
